import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, useFormState } from 'react-final-form';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Grid, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Button, { BUTTON_TYPE, BUTTON_VARIANT } from '../../../../components/Button';
import { PRIMARY_COLOR } from '../../../../theme/colorConstants';

import JobPostingDuration from './FormSections/JobPostingDuration';
import LetsGetStarted from './FormSections/LetsGetStarted';
import RecurringTwoWeeksSelector from './FormSections/RecurringTwoWeeksSelector';
import ConfirmModal from './ConfirmModal';

const CreateJobFormBody = ({ onApiSubmit, user, isProcessing }) => {
  const form = useForm();
  const formState = useFormState();
  const theme = useTheme();

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

  const currentFormValues = formState.values;

  const sectionWrapperSx = { marginBottom: 0 };

  const sectionCardSx = {
    width: '100%',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.06)',
    backgroundColor: theme.palette.common.white,
  };

  // Modal opens when Review is clicked and all validations pass. Blocked by: form errors,
  // empty jobOrders, overlaps, invalid shift duration (0 or >20h), or openings < 1.
  const reviewButtonOnClick = () => {
    setHasClickedSubmit(true);
    const fieldsWithErrors = Object.keys(formState.errors);

    // First, display form field validation errors if any exist
    // This ensures users see feedback about required fields (positionTemplate, location, etc.)
    // even before job orders are created
    if (fieldsWithErrors.length > 0) {
      form.batch(() => {
        fieldsWithErrors.forEach((fieldName) => {
          form.blur(fieldName, true);
        });
      });
      return;
    }

    // Validate job creation data - check if job orders exist and there are no overlaps
    const jobOrders = currentFormValues.jobOrders || [];
    const hasOverlap = currentFormValues.hasOverlap || false;
    const hasInvalidShiftDuration = currentFormValues.hasInvalidShiftDuration || false;

    if (jobOrders.length === 0) {
      // No job orders created yet.
      // This commonly happens when users select days but haven't set shift times yet
      // (shifts with null times are filtered out during job order creation).
      // Trigger section-level validation UI instead of silently returning.
      form.blur('recurringTwoWeeksSelector', true);
      return;
    }

    if (hasOverlap) {
      // Overlaps detected - blur selector so user sees the overlap error in RecurringTwoWeeksSelector
      form.blur('recurringTwoWeeksSelector', true);
      return;
    }

    if (hasInvalidShiftDuration) {
      // Shift duration violations (0 minutes or > 20 hours) detected, don't proceed
      form.blur('recurringTwoWeeksSelector', true);
      return;
    }

    // Validate that all job orders have openings >= 1
    const invalidJobOrders = jobOrders.filter(
      (jobOrder) => !jobOrder.numOpenings || jobOrder.numOpenings < 1
    );
    if (invalidJobOrders.length > 0) {
      // Mark the recurringTwoWeeksSelector field as touched to show validation error
      form.blur('recurringTwoWeeksSelector', true);
      return;
    }

    // All validations passed, open confirmation modal
    setIsConfirmOpen(true);
  };

  const [activeStep, setActiveStep] = useState(0);
  const [oldFormLength, setOldFormLength] = useState(0);

  useEffect(() => {
    setHasClickedSubmit(false);
  }, [formState.values]);

  const sections = [
    {
      name: 'letsGetStarted',
      Section: LetsGetStarted,
    },

    {
      name: 'jobPostingDuration',
      Section: JobPostingDuration,
    },

    {
      name: 'recurringTwoWeeksSelector',
      Section: RecurringTwoWeeksSelector,
    },
  ].filter(Boolean);

  useEffect(() => {
    const fieldsWithErrors = Object.keys(formState.errors);
    const fieldsWithValues = Object.keys(formState.values);

    if (
      fieldsWithValues.length > oldFormLength &&
      fieldsWithErrors.length === 0 &&
      activeStep < sections?.length
    ) {
      setOldFormLength(fieldsWithValues.length);
      setActiveStep(activeStep + 1);
    }
  }, [activeStep, formState.errors, formState.values, oldFormLength, sections?.length]);

  return (
    <Grid sx={{ height: '100%', padding: theme.spacing(1) }}>
      <form
        onSubmit={(e) => {
          // Submission is handled via the ConfirmModal (which builds an API payload).
          e.preventDefault();
        }}
        style={{
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: '100%',
            minHeight: '350px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {sections.slice(0, activeStep + 1).map((sectionData) => (
            <Paper
              key={sectionData?.name}
              elevation={0}
              sx={{ ...sectionCardSx, marginBottom: theme.spacing(2) }}
            >
              <sectionData.Section wrapperSx={sectionWrapperSx} user={user} />
            </Paper>
          ))}
        </Box>
        <Grid container item justifyContent="flex-end" sx={{ width: '100%' }}>
          <Button
            data-testid="modalSubmitButton"
            variant={BUTTON_VARIANT.DEFAULT}
            type={BUTTON_TYPE.BUTTON}
            text={LanguageConverterV2('job.create.review')}
            onClick={reviewButtonOnClick}
            actionColor={PRIMARY_COLOR[70]}
            disabled={hasClickedSubmit && Object.keys(formState.errors).length > 0}
          />
        </Grid>
        {isConfirmOpen && (
          <ConfirmModal
            isOpen={isConfirmOpen}
            onSubmit={onApiSubmit}
            onClose={() => setIsConfirmOpen(false)}
            isProcessing={isProcessing}
          />
        )}
      </form>
    </Grid>
  );
};

CreateJobFormBody.propTypes = {
  onApiSubmit: PropTypes.func,
  isProcessing: PropTypes.bool,
  user: PropTypes.shape({
    role: PropTypes.string,
    employer: PropTypes.shape({
      corporation: PropTypes.string,
    }),
  }),
};

export default CreateJobFormBody;
